import { type FormEvent, useEffect, useMemo, useState } from "react";
import { useEditorContext } from "@/lib/editorContext";

const toLocalInputValue = (date: Date) => {
  const tzOffset = date.getTimezoneOffset() * 60000;
  return new Date(date.getTime() - tzOffset).toISOString().slice(0, 16);
};

const formatDisplay = (date: Date | null) => {
  if (!date) return "None";
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(date);
};

const formatRangeDisplay = (start: Date | null, end: Date | null) => {
  if (!start) return "None";
  if (!end || start.getTime() === end.getTime()) {
    return formatDisplay(start);
  }
  return `${formatDisplay(start)} → ${formatDisplay(end)}`;
};

export function DateEditorModal() {
  const {
    modalItem,
    modalItemId,
    closeDateEditor,
    updateManualDate,
    resetManualDate,
    manualOverrides,
  } = useEditorContext();

  const [draftStartDate, setDraftStartDate] = useState("");
  const [draftEndDate, setDraftEndDate] = useState("");
  const [rangeEnabled, setRangeEnabled] = useState(false);

  useEffect(() => {
    if (!modalItem) return;
    const manual = manualOverrides[modalItem.id];
    const manualStartIso = manual?.start ?? manual?.date;
    const manualEndIso = manual?.end ?? null;
    const initialStart = manualStartIso ? new Date(manualStartIso) : modalItem.extractedStartDate;
    const initialEnd = manualEndIso ? new Date(manualEndIso) : modalItem.extractedEndDate;
    setDraftStartDate(initialStart ? toLocalInputValue(initialStart) : "");
    setDraftEndDate(initialEnd ? toLocalInputValue(initialEnd) : "");
    setRangeEnabled(Boolean(initialEnd));
  }, [modalItem, manualOverrides]);

  const manualOverrideRange = useMemo(() => {
    if (!modalItem) return null;
    const manual = manualOverrides[modalItem.id];
    if (!manual) return null;
    const startIso = manual.start ?? manual.date;
    const endIso = manual.end ?? null;
    const start = startIso ? new Date(startIso) : null;
    const end = endIso ? new Date(endIso) : null;
    return { start, end };
  }, [manualOverrides, modalItem]);

  if (!modalItemId || !modalItem) return null;

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!draftStartDate) return;
    let nextStart = new Date(draftStartDate);
    if (Number.isNaN(nextStart.getTime())) return;
    let nextEnd: Date | null = null;
    if (rangeEnabled && draftEndDate) {
      const parsedEnd = new Date(draftEndDate);
      if (!Number.isNaN(parsedEnd.getTime())) {
        nextEnd = parsedEnd;
      }
    }
    if (nextEnd && nextEnd.getTime() < nextStart.getTime()) {
      const swappedStart = nextEnd;
      nextEnd = nextStart;
      nextStart = swappedStart;
    }
    updateManualDate(modalItem.id, nextStart, modalItem.text, nextEnd ?? undefined);
    closeDateEditor();
  };

  const handleReset = () => {
    resetManualDate(modalItem.id);
    setRangeEnabled(Boolean(modalItem.extractedEndDate));
    if (modalItem.extractedStartDate) {
      setDraftStartDate(toLocalInputValue(modalItem.extractedStartDate));
    } else {
      setDraftStartDate("");
    }
    if (modalItem.extractedEndDate) {
      setDraftEndDate(toLocalInputValue(modalItem.extractedEndDate));
    } else {
      setDraftEndDate("");
    }
  };

  const handleToggleRange = () => {
    setRangeEnabled(prev => {
      if (prev) {
        setDraftEndDate("");
        return false;
      }
      if (!draftEndDate && draftStartDate) {
        setDraftEndDate(draftStartDate);
      }
      return true;
    });
  };

  return (
    <div
      className="fixed inset-0 z-50 grid place-items-center bg-background/80 backdrop-blur"
      onClick={closeDateEditor}
    >
      <div
        className="w-full max-w-md rounded-2xl border border-border bg-card/95 p-6 shadow-2xl"
        onClick={event => event.stopPropagation()}
      >
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-card-foreground">Edit Date</h3>
          <button
            type="button"
            onClick={closeDateEditor}
            className="cursor-pointer text-sm text-muted-foreground transition hover:-translate-y-0.5 hover:text-card-foreground"
          >
            Cancel
          </button>
        </div>
        <p className="mt-1 text-sm text-muted-foreground">{modalItem.text || "Untitled item"}</p>
        <div className="mt-4 rounded-xl border border-border/60 bg-card/80 p-3 text-sm text-muted-foreground">
          <p>
            Auto-detected date: <span className="font-medium text-card-foreground">{formatRangeDisplay(modalItem.extractedStartDate, modalItem.extractedEndDate)}</span>
          </p>
          <p>
            Manual override: <span className="font-medium text-card-foreground">{manualOverrideRange ? formatRangeDisplay(manualOverrideRange.start ?? null, manualOverrideRange.end ?? null) : "None"}</span>
          </p>
        </div>
        <form className="mt-4 space-y-3" onSubmit={handleSubmit}>
          <div className="space-y-2">
            <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Start date</label>
            <input
              className="w-full cursor-pointer rounded-xl border border-border bg-input px-3 py-2 text-sm text-foreground focus:border-ring focus:outline-none"
              type="datetime-local"
              value={draftStartDate}
              onChange={event => setDraftStartDate(event.target.value)}
            />
          </div>
          <div className="flex items-center justify-between rounded-xl border border-dashed border-border/60 px-3 py-2 text-xs text-muted-foreground">
            <p className="font-medium">Need an end date?</p>
            <button
              type="button"
              onClick={handleToggleRange}
              className="cursor-pointer rounded-full border border-border/70 px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-primary transition hover:-translate-y-0.5"
            >
              {rangeEnabled ? "Remove" : "Add"}
            </button>
          </div>
          {rangeEnabled ? (
            <div className="space-y-2">
              <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">End date</label>
              <input
                className="w-full cursor-pointer rounded-xl border border-border bg-input px-3 py-2 text-sm text-foreground focus:border-ring focus:outline-none"
                type="datetime-local"
                value={draftEndDate}
                onChange={event => setDraftEndDate(event.target.value)}
              />
              <p className="text-[11px] text-muted-foreground">
                If the end date precedes the start date, it will automatically swap.
              </p>
            </div>
          ) : null}
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="submit"
              className="flex-1 cursor-pointer rounded-xl bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground transition hover:-translate-y-0.5 hover:bg-primary/90"
            >
              Save
            </button>
            <button
              type="button"
              onClick={handleReset}
              className="cursor-pointer rounded-xl border border-border/60 px-4 py-2 text-sm font-semibold text-muted-foreground transition hover:-translate-y-0.5 hover:border-border"
            >
              Reset to Auto
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
